import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import classNames from 'classnames';

/**
 * Standardized button group component for consistent action layouts
 * Supports both horizontal and vertical layouts with primary/secondary actions
 */
const ActionButtonGroup = ({
  primaryAction,
  secondaryAction,
  primaryDisabled = false,
  layout = 'vertical', // 'vertical' or 'horizontal'
  className = ''
}) => {
  const [processing, setProcessing] = React.useState(false);
  const onClickPrimary = useCallback(e => {
    setProcessing(true);
    primaryAction.onClick(e);
  }, []);

  const layoutClasses = {
    vertical: 'd-flex flex-column align-items-center gap-3',
    horizontal: 'd-flex justify-content-around align-items-center'
  };

  return (
    <div className={`${layoutClasses[layout]} mb-4 ${className}`}>
      {primaryAction && (
        <Button
          color={primaryAction.color || 'primary'}
          onClick={onClickPrimary}
          disabled={processing || primaryDisabled}
          className={classNames(primaryAction.className, layout === 'horizontal' && 'mr-3')}
          size="lg"
        >
          {processing ? 'Loading...' : primaryAction.text}
        </Button>
      )}
      {secondaryAction && (
        <Button
          color={secondaryAction.color || 'link'}
          onClick={secondaryAction.onClick}
          className={classNames(
            secondaryAction.className,
            secondaryAction.color === 'link' && 'text-muted',
            secondaryAction.color === 'link' && layout === 'vertical' && 'p-0'
          )}
          style={secondaryAction.color != 'link' ? {} : { textDecoration: 'underline' }}
          {...(secondaryAction.color === 'link' ? {} : { size: 'lg' })}
        >
          {secondaryAction.text}
        </Button>
      )}
    </div>
  );
};

ActionButtonGroup.propTypes = {
  primaryAction: PropTypes.shape({
    text: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    color: PropTypes.string,
    className: PropTypes.string
  }),
  secondaryAction: PropTypes.shape({
    text: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    color: PropTypes.string,
    className: PropTypes.string
  }),
  primaryDisabled: PropTypes.bool,
  layout: PropTypes.oneOf(['vertical', 'horizontal']),
  className: PropTypes.string
};

export default ActionButtonGroup;
